import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { Heart, X, MapPin, SlidersHorizontal, Flame, Star, ShieldCheck, MessageCircle, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../lib/api";
import { track } from "../lib/analytics";
import BottomNav from "../components/BottomNav";
import ProfileDetailModal from "../components/ProfileDetailModal";
import { useAuth } from "../context/AuthContext";
import { COUNTRIES, RESIDENCE_COUNTRIES, LANGUAGES } from "../lib/profileOptions";

const GoalTag = ({ goal }) => {
  if (goal === "mariage")
    return <span className="bg-kora-sage/20 text-white border border-white/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-sm">Mariage</span>;
  return <span className="bg-kora-ochre/30 text-white border border-white/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-sm">Amicale</span>;
};

const Card = ({ profile, onVote, isTop, onOpenDetails }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const nopeOpacity = useTransform(x, [-140, -40], [1, 0]);
  const draggingRef = useRef(false);

  const handleDragEnd = (_, info) => {
    draggingRef.current = Math.abs(info.offset.x) > 8;
    if (info.offset.x > 120) onVote(profile, "like");
    else if (info.offset.x < -120) onVote(profile, "pass");
    setTimeout(() => { draggingRef.current = false; }, 60);
  };

  return (
    <motion.div
      data-testid="swipe-card"
      className="absolute inset-0 rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(44,30,22,0.25)] bg-gray-900 no-select cursor-pointer"
      style={isTop ? { x, rotate } : {}}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragStart={() => { draggingRef.current = true; }}
      onDragEnd={handleDragEnd}
      onClick={() => { if (isTop && !draggingRef.current) onOpenDetails(profile); }}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: isTop ? 1 : 0.95, opacity: 1 }}
      exit={{ x: x.get() > 0 ? 400 : -400, opacity: 0, transition: { duration: 0.25 } }}
    >
      <img src={resolveImg(profile.photo_url)} alt={profile.name} className="w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute inset-0 bg-gradient-to-t from-kora-ink/95 via-kora-ink/30 to-transparent pointer-events-none" />

      {isTop && (
        <>
          <motion.div style={{ opacity: likeOpacity }} className="absolute top-10 left-6 border-4 border-green-400 text-green-400 px-4 py-1 rounded-xl font-display font-black text-3xl rotate-[-15deg] pointer-events-none">J'AIME</motion.div>
          <motion.div style={{ opacity: nopeOpacity }} className="absolute top-10 right-6 border-4 border-red-400 text-red-400 px-4 py-1 rounded-xl font-display font-black text-3xl rotate-[15deg] pointer-events-none">PASSE</motion.div>
        </>
      )}

      <div className="absolute bottom-0 left-0 w-full p-6 pb-8 text-white">
        <div className="mb-3 flex items-center gap-2">
          <GoalTag goal={profile.goal} />
          {profile.presence?.online && (
            <span data-testid="card-online-badge" className="inline-flex items-center gap-1.5 bg-green-500/90 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> En ligne
            </span>
          )}
        </div>
        <h2 className="font-display font-black text-3xl flex items-center gap-2">
          {profile.name}, {profile.age}
          {profile.verified && (
            <ShieldCheck size={20} className="text-white bg-kora-sage rounded-full p-0.5" data-testid="card-verified" />
          )}
        </h2>
        <div className="flex items-center gap-1.5 mt-1 text-white/80">
          <MapPin size={15} />
          <span className="text-sm">{profile.city}{profile.residence_country ? ` → ${profile.residence_country}` : ""}</span>
          {profile.body_type && <span className="text-sm">• {profile.body_type}</span>}
        </div>
        <p className="mt-3 text-sm text-white/85 leading-relaxed line-clamp-2">{profile.bio}</p>
        {profile.interests?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {profile.interests.slice(0, 4).map((it) => (
              <span key={it} className="bg-white/20 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-medium">{it}</span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const MatchModal = ({ profile, onClose, onChat }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    className="fixed inset-0 z-[60] bg-kora-ink/80 backdrop-blur-sm flex items-center justify-center p-6"
    data-testid="match-modal"
  >
    <motion.div initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white rounded-3xl p-8 max-w-sm w-full text-center">
      <p className="font-display font-black text-3xl sm:text-4xl text-gradient-warm whitespace-nowrap">C'est un match&nbsp;!</p>
      <p className="text-kora-ink/60 mt-2">Vous et {profile.name} vous êtes plu.</p>
      <img src={resolveImg(profile.photo_url)} alt={profile.name} className="w-28 h-28 rounded-full object-cover mx-auto mt-5 border-4 border-kora-terracotta" />
      <button data-testid="match-chat-btn" onClick={onChat} className="w-full mt-6 bg-kora-terracotta text-white rounded-full py-4 font-bold hover:bg-[#C94F2B] transition-colors">
        Envoyer un message
      </button>
      <button data-testid="match-close-btn" onClick={onClose} className="w-full mt-2 text-kora-muted py-2 font-medium">Continuer à découvrir</button>
    </motion.div>
  </motion.div>
);

const IcebreakerModal = ({ profile, shared, onClose, onChat }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] bg-kora-ink/80 backdrop-blur-sm flex items-center justify-center p-6"
    data-testid="icebreaker-modal"
  >
    <motion.div initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white rounded-3xl p-8 max-w-sm w-full text-center">
      <div className="w-12 h-12 rounded-full bg-kora-sage/15 flex items-center justify-center mx-auto">
        <MessageCircle size={24} className="text-kora-sage" />
      </div>
      <p className="font-display font-black text-2xl text-kora-ink mt-4">Brisez la glace&nbsp;!</p>
      <p className="text-kora-ink/60 mt-2 text-sm">
        {shared >= 2 ? (
          <>Vous avez <span className="font-bold text-kora-terracotta">{shared} centres d'intérêt en commun</span> avec {profile?.name}. Envoyez-lui un premier message&nbsp;!</>
        ) : (
          <>Vous avez liké {profile?.name}. Faites le premier pas — envoyez-lui un message&nbsp;! {profile?.name} devra vous répondre avant que la conversation continue.</>
        )}
      </p>
      <img src={resolveImg(profile?.photo_url)} alt={profile?.name} className="w-24 h-24 rounded-full object-cover mx-auto mt-5 border-4 border-kora-sage" />
      <button data-testid="icebreaker-chat-btn" onClick={onChat} className="w-full mt-6 bg-kora-sage text-white rounded-full py-4 font-bold hover:opacity-90 transition-opacity">
        Envoyer un message
      </button>
      <button data-testid="icebreaker-close-btn" onClick={onClose} className="w-full mt-2 text-kora-muted py-2 font-medium">Continuer à découvrir</button>
    </motion.div>
  </motion.div>
);

const Discover = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [discoverLimited, setDiscoverLimited] = useState(false);
  const [discoverResetsAt, setDiscoverResetsAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const pinnedRef = useRef(null);

  useEffect(() => {
    const pinnedId = new URLSearchParams(location.search).get("user");
    if (!pinnedId) return;
    api.get(`/profiles/by-id/${pinnedId}`).then(({ data: pinned }) => {
      pinnedRef.current = pinned;
      setProfiles((prev) => [pinned, ...prev.filter((p) => p.user_id !== pinned.user_id)]);
      setDiscoverLimited(false);
    }).catch(() => {});
    navigate("/decouvrir", { replace: true });
  }, [location.search]);
  const [match, setMatch] = useState(null);
  const [icebreaker, setIcebreaker] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [filters, setFilters] = useState({ goal: "tous", gender: "", city: "", residence_country: "", languages: "", education: "tous", body_type: "tous", min_age: "", max_age: "", min_height: "", max_height: "", marital_status: "tous", has_children: "tous" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.goal !== "tous") params.goal = filters.goal;
      if (filters.gender) params.gender = filters.gender;
      if (filters.city) params.city = filters.city;
      if (filters.residence_country) params.residence_country = filters.residence_country;
      if (filters.languages) params.languages = filters.languages;
      if (filters.education !== "tous") params.education = filters.education;
      if (filters.body_type !== "tous") params.body_type = filters.body_type;
      if (filters.marital_status !== "tous") params.marital_status = filters.marital_status;
      if (filters.has_children !== "tous") params.has_children = filters.has_children;
      if (filters.min_age) params.min_age = filters.min_age;
      if (filters.max_age) params.max_age = filters.max_age;
      if (filters.min_height) params.min_height = filters.min_height;
      if (filters.max_height) params.max_height = filters.max_height;
      const { data } = await api.get("/profiles/discover", { params });
      const list = Array.isArray(data) ? data : (data.profiles || []);
      setProfiles(list);
      setDiscoverLimited(!Array.isArray(data) && !!data.limited);
      setDiscoverResetsAt(!Array.isArray(data) ? data.resets_at || null : null);
      if (pinnedRef.current) {
        const p = pinnedRef.current;
        setProfiles((prev) => [p, ...prev.filter((x) => x.user_id !== p.user_id)]);
        setDiscoverLimited(false);
      }
    } catch (e) {
      toast.error("Impossible de charger les profils.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const superRemaining = user?.super_likes_remaining ?? 0;
  const isMaster = user?.premium_plan === "master";

  const vote = async (profile, action) => {
    if (action === "superlike") {
      if (!user?.premium) {
        toast.error("Les Super Likes sont réservés aux membres Premium ✨");
        navigate("/premium");
        return;
      }
      if (!isMaster && superRemaining <= 0) {
        toast.error("Vous avez utilisé vos 5 Super Likes de la semaine.");
        return;
      }
    }
    setProfiles((prev) => prev.filter((p) => p.user_id !== profile.user_id));
    try {
      const { data } = await api.post("/swipe", { target_id: profile.user_id, action });
      if (action === "superlike") {
        if (!isMaster) setUser((u) => ({ ...u, super_likes_remaining: Math.max(0, (u.super_likes_remaining || 1) - 1) }));
        toast.success(`Super Like envoyé à ${profile.name} ⭐`);
      }
      if (data.premium_required) {
        toast("✨ Cette personne vous a déjà liké(e) ! Passez Premium pour matcher.", {
          action: { label: "Premium", onClick: () => navigate("/premium") },
        });
      }
      if (data.matched) { track("match"); setMatch({ profile: data.profile, match_id: data.match_id }); }
      else if (data.can_message) setIcebreaker({ profile: data.profile, match_id: data.match_id, shared: data.shared_count });
    } catch (e) {
      if (e.response?.status === 402 && (e.response?.data?.detail || "").includes("nouveaux profils")) {
        toast.error(e.response.data.detail);
        load();
      } else {
        toast.error(e.response?.data?.detail || "Erreur lors du swipe.");
      }
    }
  };

  const top = profiles[0];

  return (
    <div className="min-h-screen bg-kora-sand pb-28">
      <div className="max-w-md mx-auto px-5 pt-6">
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-kora-terracotta flex items-center justify-center">
              <Flame size={16} className="text-white" />
            </div>
            <span className="font-display font-bold text-xl text-kora-ink">Découvrir</span>
          </div>
          <button data-testid="filters-toggle" onClick={() => setShowFilters((s) => !s)} className="w-10 h-10 rounded-full bg-white border border-kora-line flex items-center justify-center text-kora-ink hover:border-kora-terracotta/40 transition-colors">
            <SlidersHorizontal size={18} />
          </button>
        </header>

        {showFilters && (
          <div data-testid="filters-panel" className="bg-white rounded-2xl p-4 border border-kora-line mb-4 space-y-3 animate-float-up">
            <h2 data-testid="filters-title" className="font-display font-bold text-base text-kora-ink flex items-center gap-2">
              <SlidersHorizontal size={16} className="text-kora-terracotta" /> Filtrage avancée
            </h2>
            <div className="flex gap-2">
              {["tous", "amicale", "mariage"].map((g) => (
                <button key={g} data-testid={`filter-goal-${g}`} onClick={() => setFilters((f) => ({ ...f, goal: g }))}
                  className={`flex-1 py-2 rounded-full text-xs font-bold capitalize transition-colors ${filters.goal === g ? "bg-kora-terracotta text-white" : "bg-kora-sand text-kora-ink"}`}>
                  {g}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mb-2">
              {[{k:"",l:"Tous"},{k:"homme",l:"Homme"},{k:"femme",l:"Femme"}].map((g) => (
                <button key={g.l} data-testid={`filter-gender-${g.k || "tous"}`} onClick={() => setFilters((f) => ({ ...f, gender: g.k }))}
                  className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${filters.gender === g.k ? "bg-kora-sage text-white" : "bg-kora-sand text-kora-ink"}`}>
                  {g.l}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <select data-testid="filter-city" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} className="px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none bg-white">
                <option value="">Pays d'origine</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="filter-min-age" type="number" placeholder="Âge min" value={filters.min_age} onChange={(e) => setFilters((f) => ({ ...f, min_age: e.target.value }))} className="px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none" />
              <input data-testid="filter-max-age" type="number" placeholder="Âge max" value={filters.max_age} onChange={(e) => setFilters((f) => ({ ...f, max_age: e.target.value }))} className="px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-kora-muted mb-1">Pays de résidence</p>
              <select data-testid="filter-residence" value={filters.residence_country} onChange={(e) => setFilters((f) => ({ ...f, residence_country: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none bg-white">
                <option value="">Tous</option>
                {RESIDENCE_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-kora-muted mb-1">Langue parlée</p>
              <select data-testid="filter-languages" value={filters.languages} onChange={(e) => setFilters((f) => ({ ...f, languages: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none bg-white">
                <option value="">Toutes</option>
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.emoji} {l.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2 relative">
              {!user?.premium && (
                <button data-testid="filter-height-lock" onClick={() => { toast.error("Le filtre par Taille est réservé aux membres Premium ✨"); navigate("/premium"); }} className="absolute inset-0 z-10 flex items-center justify-center gap-1.5 rounded-xl bg-white/70 backdrop-blur-[1px] text-[11px] font-bold text-kora-ink">
                  <Lock size={13} /> Taille — réservé Premium
                </button>
              )}
              <input data-testid="filter-min-height" type="number" placeholder="Taille min (cm)" disabled={!user?.premium} value={filters.min_height} onChange={(e) => setFilters((f) => ({ ...f, min_height: e.target.value }))} className="px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none" />
              <input data-testid="filter-max-height" type="number" placeholder="Taille max (cm)" disabled={!user?.premium} value={filters.max_height} onChange={(e) => setFilters((f) => ({ ...f, max_height: e.target.value }))} className="px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-kora-muted mb-1">Niveau d'étude</p>
              <select data-testid="filter-education" value={filters.education} onChange={(e) => setFilters((f) => ({ ...f, education: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none bg-white">
                <option value="tous">Tous</option>
                <option value="Primaire">Primaire</option>
                <option value="Secondaire">Secondaire</option>
                <option value="Supérieure">Supérieure</option>
              </select>
            </div>
            <div className="relative">
              <p className="text-[11px] font-bold uppercase tracking-wider text-kora-muted mb-1 flex items-center gap-1">Silhouette {!user?.premium && <Lock size={11} />}</p>
              {!user?.premium && (
                <button data-testid="filter-bodytype-lock" onClick={() => { toast.error("Le filtre par Silhouette est réservé aux membres Premium ✨"); navigate("/premium"); }} className="absolute inset-0 z-10 rounded-xl" aria-label="Silhouette réservé Premium" />
              )}
              <select data-testid="filter-bodytype" disabled={!user?.premium} value={filters.body_type} onChange={(e) => setFilters((f) => ({ ...f, body_type: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none bg-white disabled:opacity-60">
                <option value="tous">Tous</option>
                <option value="Quelques kilos en trop">Quelques kilos en trop</option>
                <option value="Ronde">Ronde</option>
                <option value="Mince">Mince</option>
                <option value="Athlétique">Athlétique</option>
                <option value="Normale">Normale</option>
              </select>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-kora-muted mb-1">Situation matrimoniale</p>
              <select data-testid="filter-marital" value={filters.marital_status} onChange={(e) => setFilters((f) => ({ ...f, marital_status: e.target.value }))} className="w-full px-3 py-2 rounded-xl border border-kora-line text-sm focus:outline-none bg-white">
                <option value="tous">Toutes</option>
                <option value="celibataire">💚 Célibataire</option>
                <option value="divorce">💔 Divorcé(e)</option>
                <option value="veuf">🕊️ Veuf / Veuve</option>
                <option value="separe">🤝 Séparé(e)</option>
                <option value="marie">💍 Marié(e)</option>
                <option value="complique">🌀 C'est compliqué</option>
              </select>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-kora-muted mb-1">Enfants</p>
              <div className="flex gap-2" data-testid="filter-children">
                {[{ k: "tous", l: "Tous" }, { k: "oui", l: "👶 Oui" }, { k: "non", l: "🚫 Non" }].map((c) => (
                  <button key={c.k} data-testid={`filter-children-${c.k}`} onClick={() => setFilters((f) => ({ ...f, has_children: c.k }))}
                    className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${filters.has_children === c.k ? "bg-kora-terracotta text-white" : "bg-kora-sand text-kora-ink"}`}>
                    {c.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Deck */}
        <div className="relative w-full" style={{ height: "65vh", maxHeight: "560px" }}>
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
            </div>
          ) : profiles.length === 0 && discoverLimited ? (
            <div data-testid="discover-limited" className="absolute inset-0 flex flex-col items-center justify-center text-center bg-white rounded-3xl border border-kora-line p-8">
              <div className="w-16 h-16 rounded-full bg-kora-ochre/15 flex items-center justify-center mb-4">
                <Lock size={26} className="text-kora-ochre" />
              </div>
              <p className="font-display font-bold text-xl text-kora-ink">Limite de 3 nouveaux profils/12h atteinte</p>
              {discoverResetsAt && (
                <p data-testid="discover-limit-reset" className="text-kora-muted text-sm mt-2">
                  Réinitialisation le {new Date(discoverResetsAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} à {new Date(discoverResetsAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
              <p className="text-kora-muted text-sm mt-1">Passez Premium pour découvrir des profils en illimité.</p>
              <button data-testid="discover-limit-premium-btn" onClick={() => navigate("/premium")} className="mt-5 bg-kora-terracotta text-white rounded-full px-6 py-3 font-semibold animate-kora-pulse">Passer Premium ✨</button>
            </div>
          ) : profiles.length === 0 ? (
            <div data-testid="no-more-profiles" className="absolute inset-0 flex flex-col items-center justify-center text-center bg-white rounded-3xl border border-kora-line p-8">
              <div className="w-16 h-16 rounded-full bg-kora-sand flex items-center justify-center mb-4">
                <Heart size={28} className="text-kora-terracotta" />
              </div>
              <p className="font-display font-bold text-xl text-kora-ink">Plus de profils pour le moment</p>
              <p className="text-kora-muted text-sm mt-1">Revenez plus tard ou ajustez vos filtres.</p>
              <button onClick={load} className="mt-5 bg-kora-terracotta text-white rounded-full px-6 py-3 font-semibold">Rafraîchir</button>
              {!user?.premium && (
                <button
                  data-testid="no-more-profiles-premium-btn"
                  onClick={() => navigate("/premium")}
                  className="mt-3 bg-kora-terracotta text-white rounded-full px-6 py-3 font-semibold animate-kora-pulse"
                >
                  Passez premium pour Découvrir les profils en illimité ✨
                </button>
              )}
            </div>
          ) : (
            <AnimatePresence>
              {profiles.slice(0, 2).reverse().map((p) => (
                <Card key={p.user_id} profile={p} onVote={vote} isTop={p.user_id === top.user_id} onOpenDetails={setSelectedProfile} />
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Action buttons */}
        {!loading && profiles.length > 0 && (
          <div className="flex justify-center items-center gap-5 mt-6">
            <button data-testid="pass-btn" onClick={() => vote(top, "pass")} className="w-14 h-14 rounded-full bg-white border border-kora-line flex items-center justify-center text-red-500 shadow-md hover:-translate-y-1 transition-transform">
              <X size={26} />
            </button>
            <div className="relative flex flex-col items-center">
              <button data-testid="superlike-btn" onClick={() => vote(top, "superlike")} className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white shadow-[0_4px_14px_rgba(56,160,255,0.5)] hover:-translate-y-1 transition-transform">
                <Star size={28} fill="white" />
              </button>
              {user?.premium && (
                <span data-testid="superlike-count" className="absolute -top-1 -right-1 bg-white text-blue-500 text-[11px] font-bold w-6 h-6 rounded-full flex items-center justify-center border border-blue-200 shadow">
                  {isMaster ? "∞" : superRemaining}
                </span>
              )}
            </div>
            <button data-testid="like-btn" onClick={() => vote(top, "like")} className="w-14 h-14 rounded-full bg-kora-terracotta flex items-center justify-center text-white shadow-[0_4px_14px_rgba(224,93,54,0.5)] hover:-translate-y-1 transition-transform">
              <Heart size={26} fill="white" />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {match && (
          <MatchModal
            profile={match.profile}
            onClose={() => setMatch(null)}
            onChat={() => navigate(`/chat/${match.match_id}`)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {icebreaker && (
          <IcebreakerModal
            profile={icebreaker.profile}
            shared={icebreaker.shared}
            onClose={() => setIcebreaker(null)}
            onChat={() => navigate(`/chat/${icebreaker.match_id}`)}
          />
        )}
      </AnimatePresence>

      {selectedProfile && (
        <ProfileDetailModal
          profile={selectedProfile}
          onClose={() => setSelectedProfile(null)}
          onAction={(action) => {
            const p = selectedProfile;
            setSelectedProfile(null);
            vote(p, action);
          }}
        />
      )}

      <BottomNav />
    </div>
  );
};

export default Discover;
